import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Badge,
    Spinner,
    Alert,
    ListGroup,
} from 'react-bootstrap';
import {
    useNavigate,
    useParams,
    useSearchParams,
    Link,
} from 'react-router-dom';
import GuideLink from '@/components/common/GuideLink';
import ActionBar from '@/components/common/ActionBar';
import { useFeature } from '@/shared/contexts/FeatureFlagContext';
import { candidacyService } from '@/shared/services/candidacyService';
import positionService from '@/shared/services/positionService';
import { HELD_CANDIDACY_STAGES } from '@/shared/utils/candidacyStage';
import PositionChoices from './PositionChoices';
import { useTerm } from '@/shared/contexts/TermContext';
import {
    StudentWindowClosed,
    StudentWindowCloses,
} from '@/components/common/StudentWindowNotice';
import { studentWindowFor } from '@/shared/utils/studentWindow';
import { useAuth } from '@/shared/contexts/AuthContext';
import ReviewChoicesCheck from './ReviewChoicesCheck';
import WizardStepIndicator from '@/components/common/WizardStepIndicator';
import useStepChangeFocus from '@/shared/hooks/useStepChangeFocus';
import useRevealAlert from '@/shared/hooks/useRevealAlert';
import useConsumeLocationState from '@/shared/hooks/useConsumeLocationState';
import useSavedProgress from '@/shared/hooks/useSavedProgress';
import useLeaveGuard from '@/shared/hooks/useLeaveGuard';
import useStudentEmploymentRules from '@/shared/hooks/useStudentEmploymentRules';
import useRefreshOnReturn from '@/shared/hooks/useRefreshOnReturn';
import SaveProgressStatus from '@/components/common/SaveProgressStatus';
import studentApplicationService from '@/shared/services/studentApplicationService';
import courseService from '@/shared/services/courseService';
import {
    getProgramAvailability,
    getProgramRequirements,
} from '@/shared/services/programService';
import { inProgramDisplayOrder } from '@/shared/utils/programOrder';
import { badgeTextFor } from '@/shared/utils/badgeText';
import EmploymentRequirementsConfirm from '@/components/hiring/EmploymentRequirementsConfirm';
import EmploymentAttestationSummary from '@/components/hiring/EmploymentAttestationSummary';
import RequirementAnswersSummary from '@/components/hiring/RequirementAnswersSummary';
import { termLabel } from '@/shared/utils/termLabel';
import { requirementAttestationsBody } from '@/shared/utils/requirementAnswers';
import { PROGRAM_DEFINITIONS, getProgramDefinition } from './programs';
import {
    AboutYou,
    AccuracyConfirmation,
    CampusEmployment,
    GeneralPool,
    RequirementAnswers,
    PositionCommitments,
    Resume,
} from './blocks';
import { saveStudentId, STUDENT_ID_ID } from './blocks/AboutYou';
import { untickAccuracyConfirmation } from './blocks/AccuracyConfirmation';
import SharedAnswersSummary from './blocks/SharedAnswersSummary';
import {
    ELIGIBILITY_ID,
    INTEREST_STATEMENT_ID,
    POSITIONS_ID,
    PROGRAM_CHOICE_ID,
    buildStudentApplicationBody,
    emptyForm,
    firstIncompleteStep,
    formFromApplication,
    getStepErrors,
    hasEnteredAnswers,
    targetForRefusal,
    MAX_POSITIONS,
} from './studentApplicationForm';
import './StudentApplicationPage.css';

const NO_STEPS = [];

const ALL_PROGRAM_KEYS = inProgramDisplayOrder(
    Object.values(PROGRAM_DEFINITIONS)
).map((definition) => definition.key);

const DAYS_OF_WEEK = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
];

// Only these can be edited (server enforces the same rule on PUT)
const EDITABLE_STATUSES = ['draft', 'submitted'];

const positionRowsById = (rows) =>
    Object.fromEntries((rows || []).map((r) => [r.applicationId, r]));

// One application per program per term, so key them by program
const byProgram = (apps) => {
    const map = {};
    (apps || []).forEach((app) => {
        map[app.program] = app;
    });
    return map;
};

// Where to send a student who has nothing left to apply to this term, or
// null. A draft isn't listed on /applications, so its card is the way in.
// Only the programs offered count.
const nothingLeftRedirect = (apps, offeredKeys) => {
    const statuses = offeredKeys.map((key) => apps[key]?.status);
    if (statuses.some((status) => !status || status === 'draft')) return null;
    const withdrawn = statuses.filter((status) => status === 'withdrawn');
    const allWithdrawn = withdrawn.length === statuses.length;
    return {
        notice: `You've already applied to every program this term. You can apply to each program once per term.${withdrawn.length && !allWithdrawn ? ' Withdrawn applications are on the Past tab.' : ''}`,
        // withdrawn applications are listed under Past
        tab: allWithdrawn ? 'past' : undefined,
    };
};

// The programs switched on, in display order. An answer that names none of
// them is not trusted: the server still refuses a switched-off program.
const offeredKeysFrom = (data) =>
    data && data.active
        ? ALL_PROGRAM_KEYS.filter((key) => data.active[key] === true)
        : ALL_PROGRAM_KEYS;

// A change to one answer clears the server messages that no longer apply: a
// student ID message stays until that field is edited, every other one goes
// with the next edit anywhere.
const clearFieldErrors = (fieldErrors, key) => {
    if (key === 'profileStudentId') {
        const { studentId: _cleared, ...rest } = fieldErrors;
        return rest;
    }
    return fieldErrors.studentId ? { studentId: fieldErrors.studentId } : {};
};

const LEAVE_UNSAVED_PROMPT =
    'Your latest changes could not be saved. Leave anyway and lose them?';
const LEAVE_DISCARD_PROMPT =
    'Your changes to this application have not been saved. Leave anyway and lose them?';
const CHOOSE_PROGRAM_NOTE =
    'Choose a program first. Your progress is saved once you have.';

function StudentApplicationForm() {
    const { currentTerm, currentTermCode, termsLoading } = useTerm();
    const { userProfile, refreshProfile } = useAuth();
    const userId = userProfile?._id;
    const navigate = useNavigate();
    // An id in the URL means the student is editing an existing application
    const { id } = useParams();
    const isEditing = Boolean(id);
    // Set when /apply sent the student here because they had already applied
    const arrival = useConsumeLocationState(['notice']);
    const [notice, setNotice] = useState(arrival.notice || null);

    // The programs taking applications. Null until the server answers; an
    // existing application is edited whatever the switches say.
    const [switchedOnKeys, setSwitchedOnKeys] = useState(
        isEditing ? ALL_PROGRAM_KEYS : null
    );
    // A new application can start only in a program whose student window is
    // open; one that has closed is left out, and when none is left the page
    // says the window is closed.
    const offeredKeys = useMemo(
        () =>
            switchedOnKeys === null || isEditing
                ? switchedOnKeys
                : switchedOnKeys.filter(
                      (key) => studentWindowFor(currentTerm, key).accepting
                  ),
        [switchedOnKeys, isEditing, currentTerm]
    );

    // The student's applications this term, by program. The server allows
    // one per program per term, so a new application checks this first.
    const [myApps, setMyApps] = useState({});
    const [checkingMyApps, setCheckingMyApps] = useState(!isEditing);
    const [myAppsError, setMyAppsError] = useState(null);
    // Only the latest check may set the result (term change or Retry)
    const myAppsRequest = useRef(0);
    // Only the first check may send the student away; a later one (the term
    // changed) could drop a form they have started
    const myAppsChecked = useRef(false);

    const [loadingExisting, setLoadingExisting] = useState(isEditing);
    const [loadError, setLoadError] = useState(null);
    const [loadAttempt, setLoadAttempt] = useState(0);
    const [loadRetryable, setLoadRetryable] = useState(false);
    const [existingStatus, setExistingStatus] = useState(null);
    const [existingTerm, setExistingTerm] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [advancing, setAdvancing] = useState(false);
    const [error, setError] = useState(null);
    // Submit sits at the bottom, so bring its error into view
    const errorRef = useRevealAlert(error);
    // The application a duplicate submit ran into (applied in another tab)
    const [conflictApp, setConflictApp] = useState(null);
    // Choose a Program is skipped when one program is open, the link names it,
    // or the application already has one (editing)
    const [skipProgram, setSkipProgram] = useState(isEditing);
    const firstStep = skipProgram ? 2 : 1;
    const [step, setStep] = useState(isEditing ? 2 : 1);
    // The id of the draft once it exists, whether loaded or created by the
    // first save. Saves read the ref, which a save in flight has already set.
    const [savedId, setSavedId] = useState(id || null);
    const savedIdRef = useRef(id || null);
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);
    // A resumed draft opens at its first step still needing answers, once
    // its answers are in the form
    const [resumeReady, setResumeReady] = useState(false);
    const [positioned, setPositioned] = useState(!isEditing);
    // The only programs choice is made before the form is shown
    const [preselected, setPreselected] = useState(isEditing);
    const [saveNote, setSaveNote] = useState(null);
    const stepTopRef = useRef(null);
    const stepContentRef = useRef(null);
    const stepAlertRef = useRef(null);
    useStepChangeFocus(step, stepTopRef, stepContentRef);

    // Every answer lives here and reaches the server only through
    // buildStudentApplicationBody.
    const positionsOn = useFeature('hiring');
    const [searchParams] = useSearchParams();
    const positionParam = searchParams.get('position');
    const programParam = searchParams.get('program');
    const [form, setForm] = useState(() => emptyForm(positionsOn));
    // What the open positions are called, for the review; not an answer
    const [positionRows, setPositionRows] = useState(null);
    const formRef = useRef(form);
    formRef.current = form;
    const [availableCourses, setAvailableCourses] = useState([]);
    const [coursesLoading, setCoursesLoading] = useState(false);
    const [courseSearch, setCourseSearch] = useState('');
    const [coursesError, setCoursesError] = useState(null);

    // What the student sees marked wrong: the steps' own checks after Next
    // was pressed, and what the server named when it refused
    const [shownErrors, setShownErrors] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [refusal, setRefusal] = useState(null);
    const [confirmationCleared, setConfirmationCleared] = useState(false);
    const [focusRequest, setFocusRequest] = useState(null);

    const { rules: employmentRules } = useStudentEmploymentRules();

    const offeredDefinitions = (offeredKeys || []).map((key) =>
        getProgramDefinition(key)
    );
    const selectedDefinition = getProgramDefinition(form.program);
    // Before a program is chosen the steps are the first program offered's
    const definition = selectedDefinition || offeredDefinitions[0] || null;
    const steps = definition ? definition.steps : NO_STEPS;
    const stepBlocks = steps[step - 1]?.blocks || [];
    const totalSteps = steps.length;
    const isLastStep = step === totalSteps;

    // Once an application exists its program can't change: a save updates
    // that application, and the server keeps its program
    const programLocked = isEditing || Boolean(savedId);
    const resubmitting = existingStatus === 'submitted';

    // Edits stay on the application's own term, not whatever term is current
    const applicationTerm = isEditing ? existingTerm : currentTermCode;

    // What the chosen program asks of the student about themself: the
    // yes/no requirement questions and whether a resume is required. Until it
    // loads nothing extra is asked. If it cannot be read the wizard says so
    // and offers a retry, because the server refuses a submit with the
    // questions unanswered.
    const [loadedRules, setLoadedRules] = useState(null);
    const [rulesAttempt, setRulesAttempt] = useState(0);
    useEffect(() => {
        if (!form.program || !applicationTerm) return undefined;
        let cancelled = false;
        const program = form.program;
        (async () => {
            try {
                const data = await getProgramRequirements(
                    program,
                    applicationTerm
                );
                if (!cancelled && data) {
                    setLoadedRules({
                        program,
                        assertions: data.studentAssertions,
                        resumeRequired: data.resumeRequired === true,
                        trainingSessions: data.trainingSessions || [],
                    });
                }
            } catch {
                if (!cancelled) setLoadedRules({ program, failed: true });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [form.program, applicationTerm, rulesAttempt]);
    const retryRules = useCallback(() => {
        setLoadedRules(null);
        setRulesAttempt((n) => n + 1);
    }, []);
    const programRules =
        loadedRules?.program === form.program ? loadedRules : null;
    const programRulesRef = useRef(null);
    programRulesRef.current = programRules;
    const withRules = (value) => ({ ...value, programRules });
    // The blocks name each position in their errors by the row the list shows
    const withRows = (value) => ({ ...withRules(value), positionRows });

    // One place every answer changes, so an answer changed after the accuracy
    // box was ticked unticks it, and a server message goes when its answer
    // does. `key` names the answer; the confirmation itself is not one.
    const change = useCallback((updater, key) => {
        if (
            key !== 'confirmsSubmissionAccuracy' &&
            formRef.current.confirmsSubmissionAccuracy === true
        ) {
            setConfirmationCleared(true);
        }
        setFieldErrors((current) => clearFieldErrors(current, key));
        setRefusal(null);
        setForm((prev) => {
            const next = updater(prev);
            return key === 'confirmsSubmissionAccuracy'
                ? next
                : untickAccuracyConfirmation(next);
        });
    }, []);

    const setField = useCallback(
        (name, value) => {
            if (name === 'confirmsSubmissionAccuracy' && value === true) {
                setConfirmationCleared(false);
            }
            change((prev) => ({ ...prev, [name]: value }), name);
        },
        [change]
    );
    // A setter that takes a value or an updater, for the blocks written
    // before the form was one object
    const fieldSetter = (name) => (updater) =>
        change(
            (prev) => ({
                ...prev,
                [name]:
                    typeof updater === 'function'
                        ? updater(prev[name])
                        : updater,
            }),
            name
        );
    const setCoursePreferences = fieldSetter('coursePreferences');
    const setPositions = fieldSetter('positions');
    const setAvailability = fieldSetter('availability');
    const setReferences = fieldSetter('references');
    const setEmploymentAttestation = fieldSetter('employmentAttestation');
    const setPriorExperience = fieldSetter('priorExperience');
    const setInterestStatement = fieldSetter('interestStatement');
    const setProgramField = useCallback(
        (field, value) =>
            change(
                (prev) => ({
                    ...prev,
                    programAnswers: { ...prev.programAnswers, [field]: value },
                }),
                'programAnswers'
            ),
        [change]
    );

    const { academicBackground, availability, coursePreferences, references } =
        form;

    const chooseProgram = (key) => {
        if (formRef.current.program === key) return;
        const chosen = getProgramDefinition(key);
        change(
            (prev) => ({
                ...prev,
                program: key,
                // Reset course preferences and the program's answers when
                // switching
                coursePreferences: [],
                positions: Array.isArray(prev.positions) ? [] : null,
                programAnswers: chosen.emptyAnswers,
            }),
            'program'
        );
    };

    // Fill the form back in with what the student already submitted
    useEffect(() => {
        if (!id) return;
        const loadExisting = async () => {
            try {
                setLoadingExisting(true);
                setLoadError(null);
                setLoadRetryable(false);
                const app =
                    await studentApplicationService.getApplicationById(id);
                // saving is owner-only on the server, so say so up front
                // (an admin can open this page but not save it)
                const ownerId = app.student?._id || app.student;
                if (String(ownerId) !== String(userId)) {
                    setLoadError(
                        'Only the student who submitted this application can edit it.'
                    );
                    return;
                }
                if (!EDITABLE_STATUSES.includes(app.status)) {
                    setLoadError(
                        'This application is being reviewed and can no longer be edited.'
                    );
                    return;
                }
                setExistingStatus(app.status);
                setResumeReady(app.status === 'draft');
                setExistingTerm(app.term);
                let positions = null;
                if (positionsOn) {
                    // the stored list, then any live candidacy it lacks (a
                    // student who applied before the list was stored)
                    let stored = (app.positions || []).map((id) => ({
                        applicationId: id,
                    }));
                    const [rows, mine] = await Promise.all([
                        positionService
                            .listPositions({
                                program: app.program,
                                term: app.term,
                            })
                            .catch(() => null),
                        candidacyService.getMine().catch(() => null),
                    ]);
                    if (rows) setPositionRows(positionRowsById(rows.positions));
                    // A submitted application's candidacies may be absent
                    // from its stored list, and a resubmit withdraws what the
                    // list lacks, so it is not edited on the list alone.
                    if (mine === null && app.status === 'submitted') {
                        setLoadError(
                            "Couldn't load your position choices. Please try again."
                        );
                        setLoadRetryable(true);
                        return;
                    }
                    if (mine) {
                        const own = (mine.candidacies || []).filter(
                            (c) =>
                                c.program === app.program && c.term === app.term
                        );
                        // A candidacy that has ended leaves the list: sent
                        // back, a withdrawn one would be applied to again,
                        // and a closed one would refuse the resubmit.
                        stored = stored.filter((p) => {
                            const c = own.find(
                                (o) => o.applicationId === p.applicationId
                            );
                            if (c?.position) p.summary = c.position;
                            return (
                                !c || HELD_CANDIDACY_STAGES.includes(c.stage)
                            );
                        });
                        // A pool match is the program's choice, not the
                        // student's, and is never withdrawn by the list
                        const live = own
                            .filter(
                                (c) =>
                                    c.source !== 'matched' &&
                                    HELD_CANDIDACY_STAGES.includes(c.stage)
                            )
                            .sort(
                                (a, b) =>
                                    (a.studentRank ?? Infinity) -
                                    (b.studentRank ?? Infinity)
                            );
                        for (const c of live) {
                            if (
                                !stored.some(
                                    (p) => p.applicationId === c.applicationId
                                )
                            ) {
                                stored.push({
                                    applicationId: c.applicationId,
                                    summary: c.position || undefined,
                                });
                            }
                        }
                    }
                    positions = stored;
                }
                setForm(formFromApplication(app, positions));
            } catch (_err) {
                setLoadError(
                    "Couldn't load your application. Please try again."
                );
                setLoadRetryable(true);
            } finally {
                setLoadingExisting(false);
            }
        };
        loadExisting();
    }, [id, userId, positionsOn, loadAttempt]);

    // Which programs are switched on. If the server cannot say, every
    // program is shown and the server refuses the one that is off.
    useEffect(() => {
        if (isEditing) return undefined;
        let cancelled = false;
        getProgramAvailability()
            .then((data) => {
                if (!cancelled) setSwitchedOnKeys(offeredKeysFrom(data));
            })
            .catch(() => {
                if (!cancelled) setSwitchedOnKeys(ALL_PROGRAM_KEYS);
            });
        return () => {
            cancelled = true;
        };
    }, [isEditing]);

    const loadMyApps = useCallback(async () => {
        if (isEditing || termsLoading || offeredKeys === null) return;
        const request = ++myAppsRequest.current;
        if (!currentTermCode || offeredKeys.length === 0) {
            setMyApps({});
            setMyAppsError(null);
            setCheckingMyApps(false);
            return;
        }
        // the spinner stays up while the page is leaving
        let leaving = false;
        try {
            setCheckingMyApps(true);
            setMyAppsError(null);
            setMyApps({});
            const apps = await studentApplicationService.getMyApplications({
                term: currentTermCode,
            });
            if (request !== myAppsRequest.current) return;
            const appsByProgram = byProgram(apps);
            const leave =
                !myAppsChecked.current &&
                nothingLeftRedirect(appsByProgram, offeredKeys);
            myAppsChecked.current = true;
            if (leave) {
                leaving = true;
                navigate('/applications', { replace: true, state: leave });
                return;
            }
            setMyApps(appsByProgram);
        } catch (_err) {
            if (request !== myAppsRequest.current) return;
            setMyAppsError(
                "Couldn't check for an application you already have. Please try again."
            );
        } finally {
            if (request === myAppsRequest.current && !leaving) {
                setCheckingMyApps(false);
            }
        }
    }, [isEditing, termsLoading, currentTermCode, offeredKeys, navigate]);

    useEffect(() => {
        loadMyApps();
    }, [loadMyApps]);

    // With one program on, its card is already selected, unless the student
    // already has an application for it. Done before the form's baseline is
    // taken, so opening the page is not an edit.
    useEffect(() => {
        if (preselected || checkingMyApps || offeredKeys === null) return;
        const wanted =
            offeredKeys.length === 1
                ? offeredKeys[0]
                : offeredKeys.find((key) => key === programParam);
        if (wanted && !myApps[wanted]) {
            chooseProgram(wanted);
            setSkipProgram(true);
            setStep(2);
        }
        setPreselected(true);
        // chooseProgram only reads refs and a stable updater
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preselected, checkingMyApps, offeredKeys, myApps]);

    // Send the student to the application they already have for a program:
    // the edit page while it can still change, otherwise the page that shows
    // its status. The automatic redirect replaces the /apply entry, which
    // would otherwise send Back straight to the redirect again.
    const goToExistingApp = useCallback(
        (app, { replace = false } = {}) => {
            const name =
                getProgramDefinition(app.program)?.full || 'peer supporter';
            if (EDITABLE_STATUSES.includes(app.status)) {
                navigate(`/apply/${app._id}/edit`, {
                    replace,
                    state: {
                        notice:
                            app.status === 'draft'
                                ? `You already started an application to be a ${name} this term. It's open here for you to finish and submit.`
                                : `You already applied to be a ${name} this term, so your application is open here for you to update.`,
                    },
                });
                return;
            }
            const notice = `You already applied to be a ${name} this term, and that application can no longer be changed. You can apply to each program once per term.`;
            // /my-applications/:id is registered only while hiring is on
            if (positionsOn) {
                navigate(`/my-applications/${app._id}`, {
                    replace,
                    state: { notice },
                });
                return;
            }
            navigate('/applications', {
                replace,
                state: {
                    notice,
                    // withdrawn applications are listed under Past
                    tab: app.status === 'withdrawn' ? 'past' : undefined,
                },
            });
        },
        [navigate, positionsOn]
    );

    // A student with an application for the only program on offer, or the one
    // the link names, goes straight to it instead of meeting a program picker
    const wantedExisting = isEditing
        ? null
        : myApps[
              offeredKeys?.length === 1
                  ? offeredKeys[0]
                  : offeredKeys?.find((key) => key === programParam)
          ];
    const redirectedRef = useRef(false);
    useEffect(() => {
        if (!preselected || !wantedExisting || redirectedRef.current) return;
        redirectedRef.current = true;
        goToExistingApp(wantedExisting, { replace: true });
    }, [preselected, wantedExisting, goToExistingApp]);

    // Load courses when program is selected
    const loadCourses = useCallback(async () => {
        if (!form.program || !applicationTerm) return;
        try {
            setCoursesLoading(true);
            setCoursesError(null);
            const data = await courseService.getEligibleCourses(
                form.program,
                applicationTerm
            );
            setAvailableCourses(
                Array.isArray(data) ? data : data.courses || []
            );
        } catch (_err) {
            setAvailableCourses([]);
            setCoursesError("Couldn't load eligible courses. Please try again");
        } finally {
            setCoursesLoading(false);
        }
    }, [form.program, applicationTerm]);

    useEffect(() => {
        loadCourses();
    }, [loadCourses]);

    // --- Course Preferences helpers ---
    const addCoursePreference = (course) => {
        if (coursePreferences.some((cp) => cp.course._id === course._id)) {
            return;
        }
        setCoursePreferences((prev) => [
            ...prev,
            { course, rank: prev.length + 1 },
        ]);
    };

    const removeCoursePreference = (index) => {
        setCoursePreferences((prev) =>
            prev
                .filter((_, i) => i !== index)
                .map((cp, i) => ({ ...cp, rank: i + 1 }))
        );
    };

    const moveCoursePreference = (index, direction) => {
        setCoursePreferences((prev) => {
            const arr = [...prev];
            const swapIndex = index + direction;
            if (swapIndex < 0 || swapIndex >= arr.length) return prev;
            [arr[index], arr[swapIndex]] = [arr[swapIndex], arr[index]];
            return arr.map((cp, i) => ({ ...cp, rank: i + 1 }));
        });
    };

    // --- Schedule helpers ---
    const addScheduleSlot = () => {
        setAvailability((prev) => ({
            ...prev,
            schedule: [
                ...prev.schedule,
                { day: 'Monday', startTime: '09:00', endTime: '10:00' },
            ],
        }));
    };

    const updateScheduleSlot = (index, field, value) => {
        setAvailability((prev) => {
            const updated = [...prev.schedule];
            updated[index] = { ...updated[index], [field]: value };
            return { ...prev, schedule: updated };
        });
    };

    const removeScheduleSlot = (index) => {
        setAvailability((prev) => ({
            ...prev,
            schedule: prev.schedule.filter((_, i) => i !== index),
        }));
    };

    // --- Reference helpers ---
    const addReference = () => {
        setReferences((prev) => [
            ...prev,
            { name: '', email: '', relationship: '' },
        ]);
    };

    const updateReference = (index, field, value) => {
        setReferences((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const removeReference = (index) => {
        setReferences((prev) => prev.filter((_, i) => i !== index));
    };

    // --- Saving progress ---
    // Saved against a draft (or the first save, which creates it). A
    // submitted application being edited is resubmitted by the server on
    // every save and must pass its full check, so it is saved only by
    // Resubmit.
    const body = buildStudentApplicationBody(
        withRules({
            ...form,
            term: applicationTerm,
        })
    );
    const canSave =
        !!form.program && !!applicationTerm && existingStatus !== 'submitted';
    // Leaving the page creates a draft only for a student who entered
    // something; one who only picked a program leaves without a prompt. The
    // position an Apply link put on the list is not something they entered.
    const onlyLinkedPosition =
        !!positionParam &&
        form.positions?.length === 1 &&
        form.positions[0].applicationId === positionParam;
    const worthSavingOnLeave =
        Boolean(savedId) ||
        hasEnteredAnswers(
            onlyLinkedPosition ? { ...form, positions: [] } : form
        );
    const persistDraft = async (payload) => {
        if (savedIdRef.current) {
            // program and term can't change on an existing application
            const { program: _program, term: _term, ...updates } = payload;
            await studentApplicationService.updateApplication(
                savedIdRef.current,
                updates
            );
            return;
        }
        const created =
            await studentApplicationService.createApplication(payload);
        savedIdRef.current = created._id;
        if (mountedRef.current) setSavedId(created._id);
    };
    const { save, markClean, hasUnsavedChanges, saveState } = useSavedProgress({
        payload: body,
        persist: persistDraft,
        canSave: canSave && worthSavingOnLeave,
    });
    // The first save creates the draft even when nothing else changed
    const saveDraft = () => save({ force: !savedIdRef.current });

    // What the form holds when it first appears (empty, preselected, or
    // loaded from the server) is the baseline; only changes from it count as
    // unsaved
    const formShown =
        !termsLoading &&
        !loadingExisting &&
        !checkingMyApps &&
        !loadError &&
        offeredKeys !== null &&
        preselected;
    const baselineSetRef = useRef(false);
    useEffect(() => {
        if (!formShown || baselineSetRef.current) return;
        baselineSetRef.current = true;
        markClean();
    }, [formShown, markClean]);

    // The position an Apply link names goes on the list as the form opens,
    // not when the Positions step is reached, so a draft saved before then
    // already holds it. It is never folded into the saved baseline: a lookup
    // that answers after a save has started leaves it an unsaved change,
    // which the next save or leaving sends.
    const positionAppliedRef = useRef(false);
    useEffect(() => {
        if (
            !formShown ||
            !positionsOn ||
            !positionParam ||
            positionAppliedRef.current ||
            !form.program ||
            !Array.isArray(form.positions)
        ) {
            return;
        }
        positionAppliedRef.current = true;
        positionService
            .listPositions({ program: form.program, term: applicationTerm })
            .then((data) => {
                if (!mountedRef.current) return;
                const row = (data.positions || []).find(
                    (r) => r.applicationId === positionParam
                );
                if (!row) return;
                setPositions((prev) =>
                    prev.length >= MAX_POSITIONS ||
                    prev.some((p) => p.applicationId === row.applicationId)
                        ? prev
                        : [
                              ...prev,
                              {
                                  applicationId: row.applicationId,
                                  summary: row,
                              },
                          ]
                );
            })
            .catch(() => {});
        // setPositions only reads refs and a stable updater
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formShown, positionsOn, positionParam, form.program, applicationTerm]);

    // The resume comes from the profile, which the student can change in
    // another tab (the Resume block links to it). Read it again when they
    // come back, and before the review shows what will be sent.
    useRefreshOnReturn(refreshProfile, formShown);
    const reviewing = formShown && isLastStep;
    const refreshProfileRef = useRef(refreshProfile);
    refreshProfileRef.current = refreshProfile;
    useEffect(() => {
        if (reviewing) refreshProfileRef.current();
    }, [reviewing]);

    useEffect(() => {
        if (positioned || !resumeReady || loadingExisting) return;
        setStep(
            firstIncompleteStep(
                steps,
                { ...formRef.current, programRules: programRulesRef.current },
                userProfile
            ) + 1
        );
        setPositioned(true);
    }, [positioned, resumeReady, loadingExisting, steps, userProfile]);

    // Move focus to the control a message is about, once its step shows
    useEffect(() => {
        if (!focusRequest) return;
        const target =
            document.getElementById(focusRequest.id) || stepAlertRef.current;
        if (target) {
            if (!target.matches('input, select, textarea, button')) {
                target.setAttribute('tabindex', '-1');
            }
            target.focus();
        }
    }, [focusRequest]);

    useLeaveGuard(
        !submitting &&
            worthSavingOnLeave &&
            (hasUnsavedChanges || saveState.status === 'saving'),
        async (proceed) => {
            if (!canSave) {
                const prompt = form.program
                    ? LEAVE_DISCARD_PROMPT
                    : LEAVE_UNSAVED_PROMPT;
                if (window.confirm(prompt)) proceed();
                return;
            }
            try {
                await saveDraft();
            } catch {
                if (window.confirm(LEAVE_UNSAVED_PROMPT)) proceed();
                return;
            }
            proceed();
        }
    );

    const goToStep = (target) => {
        setStep(target);
        setSaveNote(null);
        setShownErrors(false);
        // A refusal belongs to the step it sent the student to
        setRefusal(null);
        // Moving on is when progress is saved
        if (canSave) saveDraft().catch(() => {});
    };

    // Next: the step's own questions first, then the student ID when the
    // step is About You, then the move (which saves the draft).
    const goNext = async () => {
        const errors = getStepErrors(
            steps,
            step - 1,
            withRows(form),
            userProfile
        );
        if (errors.length > 0) {
            setShownErrors(true);
            setFocusRequest({ id: errors[0].id });
            return;
        }
        if (stepBlocks.includes('aboutYou')) {
            setAdvancing(true);
            let result;
            try {
                result = await saveStudentId(form, userProfile, refreshProfile);
            } finally {
                if (mountedRef.current) setAdvancing(false);
            }
            if (!mountedRef.current) return;
            if (!result.saved) {
                setFieldErrors((current) => ({
                    ...current,
                    [result.field]: result.message,
                }));
                setFocusRequest({ id: STUDENT_ID_ID });
                return;
            }
        }
        goToStep(step + 1);
    };

    const saveProgress = async () => {
        if (!canSave) {
            setSaveNote(CHOOSE_PROGRAM_NOTE);
            return;
        }
        setSaveNote(null);
        try {
            await saveDraft();
        } catch {
            // The status line says it failed and offers to try again
        }
    };

    // --- Submit ---
    const handleSubmit = async () => {
        // A step the student did not pass through (a resumed draft) is
        // checked too, and the student is taken to the first one open
        for (let index = 0; index < steps.length; index += 1) {
            const errors = getStepErrors(
                steps,
                index,
                withRows(form),
                userProfile
            );
            if (errors.length > 0) {
                setStep(index + 1);
                setShownErrors(true);
                setFocusRequest({ id: errors[0].id });
                return;
            }
        }
        try {
            setSubmitting(true);
            setError(null);
            setConflictApp(null);

            if (existingStatus === 'submitted') {
                const { program: _program, term: _term, ...updates } = body;
                await studentApplicationService.updateApplication(id, updates);
                markClean();
                navigate('/applications', {
                    state: {
                        success:
                            'Your changes were saved and your application was resubmitted.',
                    },
                });
                return;
            }

            if (savedIdRef.current) {
                // Saved first, so a refused submit leaves the answers in
                // the draft
                await save({ force: true });
                await studentApplicationService.submitApplication(
                    savedIdRef.current
                );
            } else {
                await studentApplicationService.createApplication({
                    ...body,
                    submitNow: true,
                });
            }
            markClean();

            navigate('/applications', {
                state: {
                    success:
                        'Your student application has been submitted successfully!',
                },
            });
        } catch (err) {
            const data = err.response?.data;
            const target = targetForRefusal(steps, form.program, data?.field);
            if (target && data?.message) {
                // Show the refusal where the answer is asked
                setFieldErrors({
                    [data.field === 'studentId' ? 'studentId' : target.id]:
                        data.message,
                });
                setRefusal({ id: target.id, message: data.message });
                setStep(target.step + 1);
                setFocusRequest({ id: target.id });
                return;
            }
            setError(
                data?.message ||
                    'Failed to submit application. Please try again.'
            );
            // Applied in another tab since the check: keep this form on
            // screen and offer the way to the one that was saved
            if (!savedIdRef.current && err.response?.status === 409) {
                try {
                    const apps = byProgram(
                        await studentApplicationService.getMyApplications({
                            term: currentTermCode,
                        })
                    );
                    // the cards show it too if they go back to step 1
                    setMyApps(apps);
                    setConflictApp(apps[form.program] || null);
                } catch (_lookupErr) {
                    // the server's message still explains it
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    // What each block receives besides the answers
    const blockContext = {
        term: applicationTerm,
        termName: termLabel(currentTerm, applicationTerm),
        isEditing,
        resubmitting,
        applicationId: savedId || undefined,
        fieldErrors,
        roleName: definition?.roleName,
        weeklyHoursLimit: employmentRules?.weeklyHoursLimit,
        internationalWeeklyHoursLimit:
            employmentRules?.internationalWeeklyHoursLimit,
        confirmationCleared,
        programRules,
        trainingSessions: programRules?.trainingSessions,
        retryRules,
        positionRows,
        availabilityElsewhere: steps.some((s) =>
            s.blocks.includes('availability')
        ),
    };
    const stepErrors = shownErrors
        ? getStepErrors(steps, step - 1, withRows(form), userProfile)
        : [];
    const errorIds = new Set(stepErrors.map((e) => e.id));
    if (refusal) errorIds.add(refusal.id);

    // --- Render: program ---
    const renderProgramSelection = () => (
        <div>
            <h2 className="h5 mb-3">Choose a Program</h2>
            <p className="text-muted mb-3">
                {programLocked
                    ? "The program can't be changed once your application is started. To apply to the other program, start a new application."
                    : 'Select the peer support program you want to apply to. Each program has different roles and expectations.'}
            </p>
            <Row
                id={PROGRAM_CHOICE_ID}
                tabIndex={-1}
                className="mb-4"
                role="radiogroup"
                aria-label="Program"
            >
                {offeredDefinitions.map((program) => {
                    const isSelected = form.program === program.key;
                    const existingApp = programLocked
                        ? null
                        : myApps[program.key];
                    const existingBadge =
                        existingApp?.status === 'draft'
                            ? 'Already started'
                            : 'Already applied';
                    let existingHint = 'Select to see your applications.';
                    if (existingApp?.status === 'draft') {
                        existingHint = 'Select to finish your application.';
                    } else if (
                        EDITABLE_STATUSES.includes(existingApp?.status)
                    ) {
                        existingHint = 'Select to open your application.';
                    }
                    // A card selects its program (a radio), or opens the
                    // application the student already has for it (a button)
                    const descriptionId = `sap-program-description-${program.key}`;
                    const selectProgram = () => {
                        if (programLocked) return;
                        if (existingApp) {
                            goToExistingApp(existingApp);
                            return;
                        }
                        chooseProgram(program.key);
                    };
                    return (
                        <Col md={6} key={program.key} className="mb-3">
                            <Card
                                role={existingApp ? 'button' : 'radio'}
                                tabIndex={programLocked ? -1 : 0}
                                aria-checked={
                                    existingApp ? undefined : isSelected
                                }
                                aria-describedby={descriptionId}
                                aria-label={
                                    existingApp
                                        ? `${program.full}. ${existingBadge}. ${existingHint}`
                                        : program.full
                                }
                                aria-disabled={programLocked || undefined}
                                className={`h-100 ${programLocked ? '' : 'program-card-choice'} ${isSelected ? 'border-primary border-2' : ''} ${programLocked && !isSelected ? 'opacity-50' : ''}`}
                                onClick={selectProgram}
                                onKeyDown={(e) => {
                                    if (e.key !== 'Enter' && e.key !== ' ') {
                                        return;
                                    }
                                    e.preventDefault();
                                    selectProgram();
                                }}
                            >
                                <Card.Body className="text-center">
                                    <Badge
                                        bg={program.bg || ''}
                                        text={badgeTextFor(program.bg || '')}
                                        className={`fs-6 px-3 py-2 mb-2 ${program.badgeClassName || ''}`}
                                    >
                                        {program.label}
                                    </Badge>
                                    <p className="fw-semibold mb-1">
                                        {program.full}
                                    </p>
                                    <p
                                        id={descriptionId}
                                        className="text-muted small mb-0"
                                    >
                                        {program.description}
                                    </p>
                                    {isSelected && (
                                        <div className="mt-2">
                                            <Badge bg="primary" pill>
                                                Selected
                                            </Badge>
                                        </div>
                                    )}
                                    {existingApp && (
                                        <div className="mt-2">
                                            <Badge bg="secondary" pill>
                                                {existingBadge}
                                            </Badge>
                                            <p className="small text-muted mb-0 mt-1">
                                                {existingHint}
                                            </p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>

            {selectedDefinition && (
                <Alert
                    variant={selectedDefinition.noteVariant}
                    className="mt-3"
                >
                    {selectedDefinition.note}
                </Alert>
            )}
        </div>
    );

    // --- Render: prior experience (SI) ---
    const renderPriorExperience = () => (
        <div>
            <Form.Group className="mb-3" controlId="sap-prior-experience">
                <Form.Label>Prior Experience</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Describe any relevant tutoring, teaching, mentoring, or peer support experience..."
                    value={form.priorExperience}
                    onChange={(e) => setPriorExperience(e.target.value)}
                />
            </Form.Group>
        </div>
    );

    // --- Render: course preferences ---
    const renderCoursePreferences = () => {
        const filteredCourses = availableCourses.filter((c) => {
            if (!courseSearch.trim()) return true;
            const search = courseSearch.toLowerCase();
            const label =
                `${c.subject} ${c.courseNumber} ${c.title}`.toLowerCase();
            return label.includes(search);
        });

        return (
            <div>
                <h2 className="h5 mb-3" id={POSITIONS_ID} tabIndex={-1}>
                    Course Preferences
                </h2>
                <p className="text-muted mb-3">
                    Select the courses you would like to support as{' '}
                    {definition.article} {definition.roleName}, then rank them
                    by preference. Your top choice should be rank 1.
                </p>

                {/* Selected courses with ranking */}
                {coursePreferences.length > 0 && (
                    <Card className="bg-light border-0 mb-4">
                        <Card.Body>
                            <h3 className="h6">
                                Your Ranked Course Preferences
                            </h3>
                            <p className="text-muted small">
                                Higher rank = higher preference. Use arrows to
                                reorder.
                            </p>
                            <ListGroup variant="flush">
                                {coursePreferences.map((cp, index) => (
                                    <ListGroup.Item
                                        key={cp.course._id}
                                        className="d-flex align-items-center justify-content-between bg-transparent"
                                    >
                                        <div className="d-flex align-items-center gap-3">
                                            <Badge
                                                bg="secondary"
                                                pill
                                                className="fs-6"
                                            >
                                                {cp.rank}
                                            </Badge>
                                            <div>
                                                <strong>
                                                    {cp.course.subject}{' '}
                                                    {cp.course.courseNumber}
                                                </strong>
                                                <span className="text-muted ms-2">
                                                    {cp.course.title}
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                disabled={index === 0}
                                                onClick={() =>
                                                    moveCoursePreference(
                                                        index,
                                                        -1
                                                    )
                                                }
                                                className="me-1"
                                            >
                                                &#9650;
                                            </Button>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                disabled={
                                                    index ===
                                                    coursePreferences.length - 1
                                                }
                                                onClick={() =>
                                                    moveCoursePreference(
                                                        index,
                                                        1
                                                    )
                                                }
                                                className="me-1"
                                            >
                                                &#9660;
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() =>
                                                    removeCoursePreference(
                                                        index
                                                    )
                                                }
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </Card.Body>
                    </Card>
                )}

                {/* Course search and list */}
                <Form.Group className="mb-3" controlId="sap-search-courses">
                    <Form.Label>Search Courses</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Search by subject, number, or title..."
                        value={courseSearch}
                        onChange={(e) => setCourseSearch(e.target.value)}
                    />
                </Form.Group>

                {coursesLoading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" />
                        <span className="ms-2 text-muted">
                            Loading courses...
                        </span>
                    </div>
                ) : coursesError ? (
                    <Alert variant="danger" className="text-center">
                        <p className="mb-2">{coursesError}</p>
                        <Button
                            type="button"
                            variant="outline-danger"
                            size="sm"
                            onClick={() => loadCourses()}
                        >
                            Retry
                        </Button>
                    </Alert>
                ) : filteredCourses.length === 0 ? (
                    <p className="text-muted text-center py-3">
                        {courseSearch.trim()
                            ? 'No courses match your search.'
                            : 'No eligible courses found for this program and term.'}
                    </p>
                ) : (
                    <div className="border rounded course-choice-list">
                        <ListGroup variant="flush">
                            {filteredCourses.map((course) => {
                                const alreadySelected = coursePreferences.some(
                                    (cp) => cp.course._id === course._id
                                );
                                return (
                                    <ListGroup.Item
                                        key={course._id}
                                        className="d-flex justify-content-between align-items-center"
                                        action={!alreadySelected}
                                        onClick={() =>
                                            !alreadySelected &&
                                            addCoursePreference(course)
                                        }
                                    >
                                        <div>
                                            <strong>
                                                {course.subject}{' '}
                                                {course.courseNumber}
                                            </strong>
                                            <span className="text-muted ms-2">
                                                {course.title}
                                            </span>
                                        </div>
                                        {alreadySelected ? (
                                            <Badge bg="success" pill>
                                                Added
                                            </Badge>
                                        ) : (
                                            <Badge
                                                bg="outline-primary"
                                                className="border border-primary text-primary"
                                                pill
                                            >
                                                + Add
                                            </Badge>
                                        )}
                                    </ListGroup.Item>
                                );
                            })}
                        </ListGroup>
                    </div>
                )}
            </div>
        );
    };

    // --- Render: interest statement (SI) ---
    const renderInterestStatement = () => (
        <div>
            <h2 className="h5 mb-3">Interest Statement</h2>
            <Form.Group className="mb-3" controlId={INTEREST_STATEMENT_ID}>
                <Form.Label>
                    Why do you want to be a peer supporter?{' '}
                    <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Describe your motivation for becoming a peer supporter, what you hope to gain from the experience, and how you plan to help students succeed..."
                    value={form.interestStatement}
                    onChange={(e) => setInterestStatement(e.target.value)}
                    required
                />
            </Form.Group>
        </div>
    );

    // --- Render: weekly availability (SI) ---
    const renderAvailability = () => (
        <div>
            <h2 className="h5 mb-3">Weekly Availability</h2>
            <Row>
                <Col md={4}>
                    <Form.Group
                        className="mb-3"
                        controlId="sap-hours-available-per-week"
                    >
                        <Form.Label>Hours Available Per Week</Form.Label>
                        <Form.Control
                            type="number"
                            min="1"
                            max="40"
                            placeholder="e.g., 10"
                            value={availability.hoursPerWeek}
                            onChange={(e) =>
                                setAvailability((prev) => ({
                                    ...prev,
                                    hoursPerWeek: e.target.value,
                                }))
                            }
                        />
                    </Form.Group>
                </Col>
            </Row>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h3 className="h6 mb-1">Schedule Availability</h3>
                    <p className="text-muted small mb-0">
                        Add time slots when you are available to lead sessions
                        or support classes.
                    </p>
                </div>
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={addScheduleSlot}
                >
                    + Add Time Slot
                </Button>
            </div>

            {availability.schedule.length === 0 && (
                <p className="text-muted text-center py-3">
                    No time slots added. Click &quot;+ Add Time Slot&quot; to
                    indicate your availability.
                </p>
            )}

            {availability.schedule.map((slot, index) => (
                <Card key={index} className="mb-2 border-0 shadow-sm">
                    <Card.Body className="py-2">
                        <Row className="align-items-center">
                            <Col md={4}>
                                <Form.Select
                                    size="sm"
                                    value={slot.day}
                                    onChange={(e) =>
                                        updateScheduleSlot(
                                            index,
                                            'day',
                                            e.target.value
                                        )
                                    }
                                >
                                    {DAYS_OF_WEEK.map((day) => (
                                        <option key={day} value={day}>
                                            {day}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Control
                                    type="time"
                                    size="sm"
                                    value={slot.startTime}
                                    onChange={(e) =>
                                        updateScheduleSlot(
                                            index,
                                            'startTime',
                                            e.target.value
                                        )
                                    }
                                />
                            </Col>
                            <Col md={1} className="text-center">
                                <span className="text-muted">to</span>
                            </Col>
                            <Col md={3}>
                                <Form.Control
                                    type="time"
                                    size="sm"
                                    value={slot.endTime}
                                    onChange={(e) =>
                                        updateScheduleSlot(
                                            index,
                                            'endTime',
                                            e.target.value
                                        )
                                    }
                                />
                            </Col>
                            <Col md={1} className="text-end">
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => removeScheduleSlot(index)}
                                >
                                    &times;
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            ))}
        </div>
    );

    // --- Render: references (SI) ---
    const renderReferences = () => (
        <div>
            <Card className="mb-3 border-0 shadow-sm">
                <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                    <strong>References (Optional)</strong>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={addReference}
                    >
                        + Add Reference
                    </Button>
                </Card.Header>
                <Card.Body>
                    {references.length === 0 ? (
                        <p className="text-muted mb-0">
                            No references added. You may add references to
                            strengthen your application.
                        </p>
                    ) : (
                        references.map((ref, index) => (
                            <Card
                                key={index}
                                className="mb-2 border-0 bg-light"
                            >
                                <Card.Body className="py-2">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <p className="mb-0 fw-semibold">
                                            Reference {index + 1}
                                        </p>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() =>
                                                removeReference(index)
                                            }
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                    <Row>
                                        <Col md={4}>
                                            <Form.Group
                                                className="mb-2"
                                                controlId="sap-name"
                                            >
                                                <Form.Label className="small">
                                                    Name
                                                </Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    placeholder="Reference name"
                                                    value={ref.name}
                                                    onChange={(e) =>
                                                        updateReference(
                                                            index,
                                                            'name',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group
                                                className="mb-2"
                                                controlId="sap-email"
                                            >
                                                <Form.Label className="small">
                                                    Email
                                                </Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="email"
                                                    placeholder="email@msudenver.edu"
                                                    value={ref.email}
                                                    onChange={(e) =>
                                                        updateReference(
                                                            index,
                                                            'email',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group
                                                className="mb-2"
                                                controlId="sap-relationship"
                                            >
                                                <Form.Label className="small">
                                                    Relationship
                                                </Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    placeholder="e.g., Professor, Advisor"
                                                    value={ref.relationship}
                                                    onChange={(e) =>
                                                        updateReference(
                                                            index,
                                                            'relationship',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        ))
                    )}
                </Card.Body>
            </Card>
        </div>
    );

    // --- Render: Review & Submit ---
    const renderReview = () => {
        const hasBlock = (name) => steps.some((s) => s.blocks.includes(name));
        const Summary = definition.Summary;
        const storedResume = form.programAnswers.resume ?? null;
        const profileResume = userProfile?.resume ?? null;
        const shownResume = resubmitting
            ? (storedResume ?? profileResume)
            : (profileResume ?? storedResume);
        const programApplication = {
            ...definition.toPayload(form.programAnswers, form),
            // A submitted application shows the copy it holds. A draft shows
            // the profile resume it will copy at submit, else a file it
            // already holds. Only a copy can be downloaded from the
            // application.
            resume: shownResume,
            _id:
                storedResume && shownResume === storedResume
                    ? savedId || undefined
                    : undefined,
        };
        // The answers as a reviewer would read them, with the hours the
        // student confirmed
        const assertions = programRules?.assertions;
        const reviewRequirementAnswers = requirementAttestationsBody(
            form.requirementAnswers,
            assertions,
            Array.isArray(form.positions)
                ? form.positions.map((p) => p.applicationId)
                : []
        ).map((entry) => ({
            ...entry,
            minimum:
                entry.rule === 'positionCommitment'
                    ? (positionRows?.[entry.position]?.hoursPerWeek ?? null)
                    : null,
        }));
        const sharedApplication = {
            generalPool: form.generalPool,
            availability: hasBlock('availability') ? undefined : availability,
            campusEmployment: form.campusEmployment,
        };

        const positionsStep =
            steps.findIndex((s) => s.blocks.includes('positions')) + 1;
        const checkRows = Array.isArray(form.positions)
            ? form.positions.map((p, index) => {
                  const row = positionRows?.[p.applicationId] || p.summary;
                  const answered =
                      form.requirementAnswers.courses[p.applicationId];
                  const asked =
                      assertions?.priorCompletion &&
                      typeof answered === 'boolean';
                  return {
                      id: p.applicationId,
                      title: row
                          ? `Choice ${index + 1}: ${row.subject} ${row.courseNumber}${row.section ? `, section ${row.section}` : ''}`
                          : `Choice ${index + 1}`,
                      courseLabel: row
                          ? `${row.subject} ${row.courseNumber}`
                          : 'the course',
                      course: asked ? answered : null,
                  };
              })
            : [];

        return (
            <div>
                <h2 className="h5 mb-3">Review &amp; Submit</h2>
                <p className="text-muted mb-4">
                    Please review your application before submitting. You can go
                    back to any step to make changes.
                </p>

                {positionsStep > 0 && (
                    <ReviewChoicesCheck
                        rows={checkRows}
                        onEdit={() => goToStep(positionsStep)}
                    />
                )}

                {/* Program */}
                <Card className="mb-3 border-0 shadow-sm">
                    <Card.Header className="bg-light">
                        <strong>Program</strong>
                    </Card.Header>
                    <Card.Body>
                        <Badge
                            bg={definition.bg || ''}
                            text={badgeTextFor(definition.bg || '')}
                            className={`fs-6 px-3 py-2 ${definition.badgeClassName || ''}`}
                        >
                            {definition.label}
                        </Badge>
                        <span className="ms-2">{definition.full}</span>
                    </Card.Body>
                </Card>

                {/* About You */}
                <Card className="mb-3 border-0 shadow-sm">
                    <Card.Header className="bg-light">
                        <strong>Academic Background</strong>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={8}>
                                <small className="text-muted">Major</small>
                                <p className="mb-1">
                                    {academicBackground.major || 'Not provided'}
                                </p>
                                {academicBackground.secondMajor && (
                                    <>
                                        <small className="text-muted">
                                            Second major
                                        </small>
                                        <p className="mb-1">
                                            {academicBackground.secondMajor}
                                        </p>
                                    </>
                                )}
                                {academicBackground.minor && (
                                    <>
                                        <small className="text-muted">
                                            Minor
                                        </small>
                                        <p className="mb-1">
                                            {academicBackground.minor}
                                        </p>
                                    </>
                                )}
                            </Col>
                        </Row>
                        {hasBlock('priorExperience') &&
                            form.priorExperience && (
                                <>
                                    <small className="text-muted">
                                        Prior Experience
                                    </small>
                                    <p className="mb-0">
                                        {form.priorExperience}
                                    </p>
                                </>
                            )}
                    </Card.Body>
                </Card>

                {/* Course Preferences */}
                <Card className="mb-3 border-0 shadow-sm">
                    <Card.Header className="bg-light">
                        <strong>
                            {Array.isArray(form.positions)
                                ? 'Position choices'
                                : 'Course Preferences'}
                        </strong>
                    </Card.Header>
                    <Card.Body>
                        {Array.isArray(form.positions) ? (
                            form.positions.length === 0 ? (
                                <p className="text-muted mb-0">
                                    No positions chosen.
                                </p>
                            ) : (
                                <ListGroup variant="flush">
                                    {form.positions.map((p, index) => {
                                        const row =
                                            positionRows?.[p.applicationId] ||
                                            p.summary;
                                        return (
                                            <ListGroup.Item
                                                key={p.applicationId}
                                                className="bg-transparent px-0"
                                            >
                                                <Badge
                                                    bg="secondary"
                                                    pill
                                                    className="me-2"
                                                >
                                                    {index + 1}
                                                </Badge>
                                                {row
                                                    ? `${row.subject} ${row.courseNumber}${row.section ? `, section ${row.section}` : ''} - ${row.title}`
                                                    : positionRows
                                                      ? 'A position that is no longer open'
                                                      : 'A position you chose'}
                                            </ListGroup.Item>
                                        );
                                    })}
                                </ListGroup>
                            )
                        ) : coursePreferences.length === 0 ? (
                            <p className="text-muted mb-0">
                                No courses selected.
                            </p>
                        ) : (
                            <ListGroup variant="flush">
                                {coursePreferences.map((cp) => (
                                    <ListGroup.Item
                                        key={cp.course._id}
                                        className="bg-transparent px-0"
                                    >
                                        <Badge
                                            bg="secondary"
                                            pill
                                            className="me-2"
                                        >
                                            {cp.rank}
                                        </Badge>
                                        {cp.course.subject}{' '}
                                        {cp.course.courseNumber} -{' '}
                                        {cp.course.title}
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        )}
                    </Card.Body>
                </Card>

                {reviewRequirementAnswers.length > 0 && (
                    <Card className="mb-3 border-0 shadow-sm">
                        <Card.Header className="bg-light">
                            <strong>Your answers for each position</strong>
                        </Card.Header>
                        <Card.Body>
                            <RequirementAnswersSummary
                                own
                                answers={reviewRequirementAnswers}
                                labelFor={(entry) => {
                                    const index = form.positions.findIndex(
                                        (p) =>
                                            p.applicationId === entry.position
                                    );
                                    const position = form.positions[index];
                                    const row =
                                        positionRows?.[
                                            position?.applicationId
                                        ] || position?.summary;
                                    return row
                                        ? `${row.subject} ${row.courseNumber}`
                                        : `the course for choice ${index + 1}`;
                                }}
                            />
                        </Card.Body>
                    </Card>
                )}

                {/* Interest & Availability */}
                {hasBlock('interestStatement') && (
                    <Card className="mb-3 border-0 shadow-sm">
                        <Card.Header className="bg-light">
                            <strong>Interest &amp; Availability</strong>
                        </Card.Header>
                        <Card.Body>
                            <small className="text-muted">
                                Interest Statement
                            </small>
                            <p className="mb-2">
                                {form.interestStatement || 'Not provided'}
                            </p>
                            {availability.hoursPerWeek && (
                                <>
                                    <small className="text-muted">
                                        Hours Per Week
                                    </small>
                                    <p className="mb-2">
                                        {availability.hoursPerWeek}
                                    </p>
                                </>
                            )}
                            {availability.schedule.length > 0 && (
                                <>
                                    <small className="text-muted">
                                        Schedule
                                    </small>
                                    <ul className="mb-0">
                                        {availability.schedule.map(
                                            (slot, i) => (
                                                <li key={i}>
                                                    {slot.day}: {slot.startTime}{' '}
                                                    - {slot.endTime}
                                                </li>
                                            )
                                        )}
                                    </ul>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                )}

                <SharedAnswersSummary application={sharedApplication} />

                {/* Program-Specific */}
                <Summary application={programApplication} audience="student" />

                <EmploymentAttestationSummary
                    attestation={form.employmentAttestation}
                />
            </div>
        );
    };

    // --- Step routing ---
    const renderBlock = (block) => {
        switch (block) {
            case 'program':
                return renderProgramSelection();
            case 'aboutYou':
                return (
                    <AboutYou
                        answers={form}
                        onChange={setField}
                        errorIds={errorIds}
                        context={blockContext}
                    />
                );
            case 'priorExperience':
                return renderPriorExperience();
            case 'positions':
                return Array.isArray(form.positions) ? (
                    <PositionChoices
                        program={form.program}
                        term={applicationTerm}
                        chosen={form.positions}
                        setChosen={setPositions}
                        article={definition.article}
                        roleName={definition.roleName}
                        invalid={stepErrors.some((e) => e.id === POSITIONS_ID)}
                        onRows={(rows) =>
                            setPositionRows(positionRowsById(rows))
                        }
                    />
                ) : (
                    renderCoursePreferences()
                );
            case 'requirementAnswers':
                return (
                    <RequirementAnswers
                        answers={form}
                        onChange={setField}
                        errorIds={errorIds}
                        context={blockContext}
                    />
                );
            case 'positionCommitments':
                return (
                    <PositionCommitments
                        answers={form}
                        onChange={setField}
                        errorIds={errorIds}
                        context={blockContext}
                    />
                );
            case 'resume':
                return (
                    <Resume
                        answers={form}
                        errorIds={errorIds}
                        context={blockContext}
                    />
                );
            case 'generalPool':
                return (
                    <GeneralPool
                        answers={form}
                        onChange={setField}
                        errorIds={errorIds}
                        context={blockContext}
                    />
                );
            case 'interestStatement':
                return renderInterestStatement();
            case 'availability':
                return renderAvailability();
            case 'programQuestions': {
                const Questions = definition.Questions;
                return (
                    <Questions
                        answers={form.programAnswers}
                        onChange={setProgramField}
                        errorIds={errorIds}
                        context={blockContext}
                    />
                );
            }
            case 'programEligibility': {
                const Eligibility = definition.EligibilityQuestions;
                return Eligibility ? (
                    <Eligibility
                        answers={form.programAnswers}
                        onChange={setProgramField}
                        errorIds={errorIds}
                        context={blockContext}
                    />
                ) : null;
            }
            case 'eligibility':
                return (
                    <div id={ELIGIBILITY_ID}>
                        <EmploymentRequirementsConfirm
                            value={form.employmentAttestation}
                            onChange={setEmploymentAttestation}
                        />
                    </div>
                );
            case 'campusEmployment':
                return (
                    <CampusEmployment
                        answers={form}
                        onChange={setField}
                        errorIds={errorIds}
                        context={blockContext}
                    />
                );
            case 'review':
                return renderReview();
            case 'references':
                return renderReferences();
            default:
                return null;
        }
    };

    const renderStep = () => (
        <div>
            {(stepErrors.length > 0 || refusal) && (
                <Alert
                    ref={stepAlertRef}
                    tabIndex={-1}
                    variant="danger"
                    role="alert"
                >
                    {refusal ? (
                        refusal.message
                    ) : (
                        <>
                            Answer these before continuing:
                            <ul className="mb-0">
                                {stepErrors.map((e) => (
                                    <li key={e.id}>
                                        <a
                                            href={`#${e.id}`}
                                            className="alert-link"
                                            onClick={(event) => {
                                                event.preventDefault();
                                                setFocusRequest({ id: e.id });
                                            }}
                                        >
                                            {e.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </Alert>
            )}
            {step === firstStep && !isEditing && (
                <Alert variant="info" role="note" className="mb-4">
                    <p className="fw-semibold mb-1">
                        Before you start, have ready:
                    </p>
                    <ul className="mb-0">
                        <li>Your 900 number</li>
                        <li>
                            {programRules?.resumeRequired === true
                                ? 'A resume (PDF or Word)'
                                : 'A resume (PDF or Word), if you have one'}
                        </li>
                        <li>About 15 minutes</li>
                    </ul>
                </Alert>
            )}
            {stepBlocks.map((block, index) => (
                <div key={block} className={index > 0 ? 'mt-4' : undefined}>
                    {renderBlock(block)}
                </div>
            ))}
            {isLastStep && (
                <div className="mt-4">
                    <AccuracyConfirmation
                        answers={form}
                        onChange={setField}
                        errorIds={errorIds}
                        context={blockContext}
                    />
                </div>
            )}
        </div>
    );

    if (
        termsLoading ||
        loadingExisting ||
        checkingMyApps ||
        offeredKeys === null
    ) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Loading...</p>
            </Container>
        );
    }

    if (myAppsError) {
        return (
            <Container className="py-4">
                <Button
                    variant="link"
                    as={Link}
                    to="/applications"
                    className="ps-0 mb-2"
                >
                    &larr; Back to Applications
                </Button>
                <Alert variant="danger">
                    <p className="mb-2">{myAppsError}</p>
                    <Button
                        type="button"
                        variant="outline-danger"
                        size="sm"
                        onClick={() => loadMyApps()}
                    >
                        Retry
                    </Button>
                </Alert>
            </Container>
        );
    }

    if (loadError) {
        return (
            <Container className="py-4">
                <Button
                    variant="link"
                    as={Link}
                    to="/applications"
                    className="ps-0 mb-2"
                >
                    &larr; Back to Applications
                </Button>
                <Alert variant="danger">
                    <p className={loadRetryable ? 'mb-2' : 'mb-0'}>
                        {loadError}
                    </p>
                    {loadRetryable && (
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => setLoadAttempt((n) => n + 1)}
                        >
                            Try again
                        </Button>
                    )}
                </Alert>
            </Container>
        );
    }

    const windowClosedPrograms = isEditing
        ? existingTerm === currentTermCode &&
          form.program &&
          !studentWindowFor(currentTerm, form.program).accepting
            ? [form.program]
            : []
        : offeredKeys.length === 0
          ? switchedOnKeys
          : [];
    if (windowClosedPrograms.length > 0) {
        return (
            <Container className="py-4">
                <Button
                    variant="link"
                    as={Link}
                    to="/applications"
                    className="ps-0 mb-2"
                >
                    &larr; Back to Applications
                </Button>
                <StudentWindowClosed
                    term={currentTerm}
                    programs={windowClosedPrograms}
                />
            </Container>
        );
    }

    if (!isEditing && offeredKeys.length === 0) {
        return (
            <Container className="py-4">
                <Button
                    variant="link"
                    as={Link}
                    to="/applications"
                    className="ps-0 mb-2"
                >
                    &larr; Back to Applications
                </Button>
                <Alert variant="info">
                    No program is taking student applications right now.
                </Alert>
            </Container>
        );
    }

    const offeredRoles = offeredDefinitions.map((d) => d.roleName);

    return (
        <Container className="py-4">
            {/* Header */}
            <div className="mb-4">
                <Button
                    variant="link"
                    as={Link}
                    to="/applications"
                    className="ps-0 mb-2"
                >
                    &larr; Back to Applications
                </Button>
                <h1>
                    {resubmitting
                        ? 'Edit Student Application'
                        : 'Student Application'}
                </h1>
                <p className="text-muted">
                    {resubmitting
                        ? 'Your saved answers are filled in below. Update anything that changed, then resubmit.'
                        : isEditing
                          ? 'Your saved answers are filled in below. Finish the remaining steps, then submit.'
                          : `Apply to become a peer supporter (${offeredRoles.join(' or ')}) for ${currentTerm?.name || (currentTermCode ? `the ${currentTermCode} term` : 'the current term')}.`}
                </p>
                <StudentWindowCloses
                    term={currentTerm}
                    programs={
                        isEditing && form.program ? [form.program] : offeredKeys
                    }
                    className="text-muted"
                />
                <GuideLink audience="student" className="small" short />
            </div>

            {notice && (
                <Alert
                    variant="info"
                    dismissible
                    onClose={() => setNotice(null)}
                >
                    {notice}
                </Alert>
            )}

            {error && (
                <Alert
                    ref={errorRef}
                    tabIndex={-1}
                    variant="danger"
                    dismissible
                    onClose={() => {
                        setError(null);
                        setConflictApp(null);
                    }}
                >
                    {error}
                    {conflictApp && (
                        <div className="mt-2">
                            <p className="mb-2">
                                Opening it leaves this page, and the answers
                                here are not saved.
                            </p>
                            <Button
                                type="button"
                                variant="outline-danger"
                                size="sm"
                                onClick={() => goToExistingApp(conflictApp)}
                            >
                                Go to your application
                            </Button>
                        </div>
                    )}
                </Alert>
            )}

            <WizardStepIndicator
                ref={stepTopRef}
                total={totalSteps - firstStep + 1}
                current={step - firstStep + 1}
                labels={steps.slice(firstStep - 1).map((s) => s.label)}
            />

            {/* Step Content */}
            <Card ref={stepContentRef} className="border-0 shadow-sm mb-4">
                <Card.Body className="p-4">{renderStep()}</Card.Body>
            </Card>

            {/* Navigation */}
            <ActionBar
                label="Application steps"
                primary={
                    !isLastStep ? (
                        <Button
                            variant="primary"
                            onClick={goNext}
                            disabled={advancing}
                        >
                            Next
                        </Button>
                    ) : (
                        <Button
                            variant="success"
                            onClick={handleSubmit}
                            disabled={submitting}
                        >
                            {submitting ? (
                                <>
                                    <Spinner
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                    />
                                    Submitting...
                                </>
                            ) : resubmitting ? (
                                'Resubmit Application'
                            ) : (
                                'Submit Application'
                            )}
                        </Button>
                    )
                }
                secondary={[
                    <Button
                        key="previous"
                        variant="outline-primary"
                        onClick={() => goToStep(step - 1)}
                        disabled={step <= firstStep}
                    >
                        Previous
                    </Button>,
                    !resubmitting && (
                        <Button
                            key="save"
                            variant="outline-primary"
                            onClick={saveProgress}
                        >
                            Save progress
                        </Button>
                    ),
                ]}
            />
            {!resubmitting && (
                <div className="mt-2">
                    <SaveProgressStatus
                        saveState={saveState}
                        onRetry={() => saveDraft().catch(() => {})}
                        note={saveNote}
                        idleText={
                            savedId
                                ? 'Your progress is saved each time you move to another step.'
                                : 'Your application is saved as a draft once you move past the first step.'
                        }
                    />
                </div>
            )}
        </Container>
    );
}

// /apply and /apply/:id/edit render this same page, so key it by the id.
// Going from one to the other then starts a fresh form instead of keeping
// the old one's state (and the notice the redirect passed along).
function StudentApplicationPage() {
    const { id } = useParams();
    return <StudentApplicationForm key={id || 'new'} />;
}

export default StudentApplicationPage;
